// Tenantra Inc. - Team Invitation Accept Page (Public)
// For NEW users who don't have an account yet
import { useState, useEffect } from 'react';
import { Users, Loader2, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/services/api';
import { setTokens, setUser } from '@/services/auth';
import { toast } from 'sonner';

export default function AcceptInvitePage() {
  // Get token from URL using vanilla JS (MPA style)
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link. No token provided.');
      setLoading(false);
      return;
    }
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const res = await api.get(`/teams/invitations/${token}/validate`);
      setInvitation(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await api.post(`/teams/invitations/${token}/accept-new`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        password: formData.password
      });
      
      // Store tokens and user via the auth service so the member stays signed in
      setTokens(res.data.access_token, res.data.refresh_token);
      setUser(res.data.user);
      
      toast.success('Welcome to the team!');
      // Use vanilla JS navigation (MPA style)
      window.location.href = '/landlord/dashboard';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join team');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-teal-600 animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-600">Validating invitation...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg border border-slate-200 p-6 max-w-md w-full text-center">
          <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-6 h-6 text-red-500" />
          </div>
          <h1 className="text-base font-semibold text-slate-900 mb-2">Invalid Invitation</h1>
          <p className="text-sm text-slate-500 mb-4">{error}</p>
          <Button 
            variant="outline" 
            onClick={() => window.location.href = '/'}
            className="h-9 text-sm"
          >
            Go to Homepage
          </Button>
        </div>
      </div>
    );
  }

  // Success - show form
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm w-full max-w-md">
        {/* Header */}
        <div className="p-5 border-b border-slate-200 text-center">
          <div className="w-12 h-12 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-3">
            <Users className="w-6 h-6 text-teal-600" />
          </div>
          <h1 className="text-base font-semibold text-slate-900">Join {invitation?.team_name}</h1>
          <p className="text-xs text-slate-500 mt-1">
            You've been invited by <span className="font-medium">{invitation?.inviter_name}</span>
          </p>
          {Array.isArray(invitation?.permissions) && (
            <p className="text-xs text-slate-500 mt-1" data-testid="invite-access-note">
              You'll have access to {invitation.permissions.length} dashboard section{invitation.permissions.length === 1 ? '' : 's'}.
            </p>
          )}
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Your Email</p>
            <p className="text-sm font-medium text-slate-900">{invitation?.email}</p>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">First Name</Label>
              <Input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                placeholder="John"
                required
                className="h-9 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Last Name</Label>
              <Input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                placeholder="Doe"
                required
                className="h-9 text-sm"
              />
            </div>
          </div>
          
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">Create Password</Label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Min. 8 characters"
                required
                className="h-9 text-sm pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">Confirm Password</Label>
            <Input
              type={showPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              placeholder="Re-enter password"
              required
              className="h-9 text-sm"
            />
          </div>
          
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-10 bg-teal-600 hover:bg-teal-700 text-white text-sm"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Joining...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Join Team
              </>
            )}
          </Button>
          
          <p className="text-[10px] text-slate-400 text-center">
            By joining, you agree to Tenantra's Terms of Service and Privacy Policy.
          </p>
        </form>
      </div>
    </div>
  );
}
